import { useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataRetailer,
  type IMasterDataRetailer,
  type MasterDataRetailer as MasterDataRetailerEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
} from "./masterDataTable.shared";

export interface MasterDataRetailerChildTarget {
  retailerId: number;
  retailerName: string;
  count: number;
}

export interface MasterDataRetailerRow extends IMasterDataRetailer {
  key: string;
}

function mapRetailersToRows(
  retailers: MasterDataRetailerEntity[],
): MasterDataRetailerRow[] {
  return retailers.map((entity) => ({
    ...MasterDataRetailer.toJSON(entity),
    key: String(entity.retailerId),
  }));
}

function toChildTarget(
  retailer: MasterDataRetailerEntity,
  count: number,
): MasterDataRetailerChildTarget {
  return {
    retailerId: retailer.retailerId,
    retailerName:
      retailer.retailerName?.trim() ||
      retailer.retailerKey ||
      String(retailer.retailerId),
    count,
  };
}

function renderCountButton(
  target: MasterDataRetailerChildTarget,
  ariaKind: string,
  onView: (target: MasterDataRetailerChildTarget) => void,
) {
  const viewLabel = `View(${target.count})`;
  return (
    <Flex align="center" justifyContent="start">
      <Button
        size="small"
        aria-label={`${viewLabel} ${ariaKind} for ${target.retailerName}`}
        onPress={() => onView(target)}
      >
        {viewLabel}
      </Button>
    </Flex>
  );
}

function buildColumns(
  onEdit: (retailer: MasterDataRetailerEntity) => void,
  onViewMatchRules: (target: MasterDataRetailerChildTarget) => void,
  onViewRawAssociations: (target: MasterDataRetailerChildTarget) => void,
  retailersById: Map<number, MasterDataRetailerEntity>,
): TableColumnType<MasterDataRetailerRow>[] {
  return [
    {
      title: "Retailer Name",
      dataIndex: "retailerName",
      key: "retailerName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 280,
      fixed: "left",
    },
    {
      title: "Retailer Key",
      dataIndex: "retailerKey",
      key: "retailerKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 240,
    },
    {
      title: "Match Rules",
      dataIndex: "matchRulesCount",
      key: "matchRulesCount",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
      cellRender: (_value: unknown, record: MasterDataRetailerRow) => {
        const retailer = retailersById.get(record.retailerId);
        if (!retailer) return null;
        return renderCountButton(
          toChildTarget(retailer, retailer.matchRulesCount ?? 0),
          "match rules",
          onViewMatchRules,
        );
      },
    },
    {
      title: "Raw Associations",
      dataIndex: "rawAssociationsCount",
      key: "rawAssociationsCount",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 160,
      cellRender: (_value: unknown, record: MasterDataRetailerRow) => {
        const retailer = retailersById.get(record.retailerId);
        if (!retailer) return null;
        return renderCountButton(
          toChildTarget(retailer, retailer.rawAssociationsCount ?? 0),
          "raw associations",
          onViewRawAssociations,
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "retailerId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => {
        const id = Number(value);
        const retailer = retailersById.get(id);
        if (!retailer) return null;
        return (
          <HasAnyPermission
            requiredPermissions={[Permissions.MASTER_DATA_EDIT]}
          >
            <Flex align="center" justifyContent="center">
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label={`Edit ${retailer.retailerName}`}
                onPress={() => onEdit(retailer)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </Flex>
          </HasAnyPermission>
        );
      },
    },
  ];
}

interface MasterDataRetailersTableProps {
  retailers: MasterDataRetailerEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (retailer: MasterDataRetailerEntity) => void;
  onViewMatchRules: (target: MasterDataRetailerChildTarget) => void;
  onViewRawAssociations: (target: MasterDataRetailerChildTarget) => void;
}

export const MasterDataRetailersTable = ({
  retailers,
  search,
  setSearch,
  setPage,
  onEdit,
  onViewMatchRules,
  onViewRawAssociations,
}: MasterDataRetailersTableProps) => {
  const retailersById = useMemo(() => {
    const map = new Map<number, MasterDataRetailerEntity>();
    for (const retailer of retailers) {
      map.set(retailer.retailerId, retailer);
    }
    return map;
  }, [retailers]);

  const columns = useMemo(
    () =>
      buildColumns(
        onEdit,
        onViewMatchRules,
        onViewRawAssociations,
        retailersById,
      ),
    [onEdit, onViewMatchRules, onViewRawAssociations, retailersById],
  );
  const dataSource = useMemo(() => mapRetailersToRows(retailers), [retailers]);

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="retailers"
      ariaLabel="Retailers table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
